import { CCard, CCardBody, CCardHeader, CCol, CRow } from "@coreui/react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  CreditNote,
  CREDIT_NOTE_STATUS_APPROVED_BY_SET,
  CREDIT_NOTE_STATUS_CANCELLATION_FAILED,
  CREDIT_NOTE_STATUS_CANCELLATION_REQUESTED,
  CREDIT_NOTE_STATUS_CANCELLED,
  CREDIT_NOTE_STATUS_CHOICES,
  CREDIT_NOTE_STATUS_ERROR,
  CREDIT_NOTE_STATUS_RECOVERABLE_ERROR,
  CREDIT_NOTE_STATUS_RESEND_REQUESTED,
  CREDIT_NOTE_STATUS_SET_UNKNOWN_ERROR,
  CREDIT_NOTE_STATUS_UPLOADED_TO_SET,
  formatCreditNoteNumber,
} from "../../models/credit-note";
import { CLIENT_TYPE_CHOICES } from "../../models/client";
import {
  ASSOCIATED_DOCUMENT_TYPE_CHOICES,
  ASSOCIATED_DOCUMENT_TYPE_ELECTRONIC_INVOICE,
} from "../../models/associated-document";
import { CURRENCY_CHOICES, PYG } from "../../currency/available-currencies";
import { formatToCurrency, formatToPyg } from "../../currency/format";
import { IVA_TYPE_CHOICES } from "../../models/iva";
import { formatUTCDateTime } from "../../utils/dates";
import { getItem } from "../../api/generics";
import { Invoice, formatInvoiceNumber } from "../../models/invoice";
import { SUCCESS } from "../../utils/constants/tags";

interface CreditNoteDetailViewProps {
  creditNote: CreditNote;
}

const getStatusBadgeClassName = (status?: string): string => {
  if (status === CREDIT_NOTE_STATUS_UPLOADED_TO_SET) return "badge-warning";
  if (
    status === CREDIT_NOTE_STATUS_ERROR ||
    status === CREDIT_NOTE_STATUS_RECOVERABLE_ERROR
  )
    return "badge-danger";
  if (status === CREDIT_NOTE_STATUS_SET_UNKNOWN_ERROR) return "badge-danger";
  if (status === CREDIT_NOTE_STATUS_APPROVED_BY_SET) return "badge-success";
  if (status === CREDIT_NOTE_STATUS_CANCELLATION_REQUESTED)
    return "badge-light";
  if (status === CREDIT_NOTE_STATUS_CANCELLED) return "badge-info";
  if (status === CREDIT_NOTE_STATUS_CANCELLATION_FAILED) return "badge-danger";
  if (status === CREDIT_NOTE_STATUS_RESEND_REQUESTED) return "badge-warning";
  return "badge-info";
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <p className="mb-1">
    <strong>{label}:</strong> {children || "-"}
  </p>
);

const CreditNoteDetailView: React.FC<CreditNoteDetailViewProps> = ({
  creditNote,
}) => {
  const currency = creditNote.currency || PYG;

  const isElectronicInvoice =
    creditNote.associatedDocumentType ===
    ASSOCIATED_DOCUMENT_TYPE_ELECTRONIC_INVOICE;

  const [associatedInvoice, setAssociatedInvoice] = useState<
    Invoice | undefined
  >(undefined);
  const [loadingAssociated, setLoadingAssociated] = useState(false);

  useEffect(() => {
    if (!isElectronicInvoice || !creditNote.associatedDocumentCdc) {
      setAssociatedInvoice(undefined);
      return;
    }
    const fetch = async () => {
      setLoadingAssociated(true);
      const params = new Map<string, string>();
      params.set("cdc", creditNote.associatedDocumentCdc!);
      const result = await getItem<Invoice>("/invoices/get_from_params/", params);
      if (result.status === SUCCESS && result.data) {
        setAssociatedInvoice(result.data);
      }
      setLoadingAssociated(false);
    };
    fetch();
  }, [isElectronicInvoice, creditNote.associatedDocumentCdc]);

  const clientAddress = [
    creditNote.clientAddress,
    creditNote.clientCityDescription,
    creditNote.clientDistrictDescription,
    creditNote.clientDepartmentDescription,
  ]
    .filter(Boolean)
    .join(", ");

  const hasSetResponse = creditNote.setResponseCode || creditNote.setError;

  return (
    <div>
      {/* Documento */}
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Documento</strong>
        </CCardHeader>
        <CCardBody>
          <CRow>
            {creditNote.id && (
              <CCol md="6">
                <Field label="ID">
                  <strong>#{creditNote.id}</strong>
                </Field>
              </CCol>
            )}
            {creditNote.creditNoteNumber && (
              <CCol md="6">
                <Field label="Número de Nota de Crédito">
                  {formatCreditNoteNumber(creditNote)}
                </Field>
              </CCol>
            )}
            <CCol md="6">
              <Field label="Timbrado">{creditNote.stampIdentifier}</Field>
            </CCol>
            <CCol md="6">
              <Field label="Establecimiento">
                {creditNote.establishmentCode}
              </Field>
            </CCol>
            <CCol md="6">
              <Field label="Punto de Expedición">
                {creditNote.dispatchPointIdentifier}
              </Field>
            </CCol>
            <CCol md="6">
              <Field label="Fecha">
                {creditNote.creditNoteDate
                  ? formatUTCDateTime(new Date(creditNote.creditNoteDate))
                  : "-"}
              </Field>
            </CCol>
            <CCol md="6">
              <Field label="Estado">
                <span
                  className={`badge badge-pill ${getStatusBadgeClassName(creditNote.status)}`}
                  style={{ fontSize: "13px" }}
                >
                  {CREDIT_NOTE_STATUS_CHOICES.get(creditNote.status!) ||
                    creditNote.status}
                </span>
              </Field>
            </CCol>
            {creditNote.cdc && (
              <CCol md="12">
                <Field label="CDC">
                  <code style={{ wordBreak: "break-all" }}>
                    {creditNote.cdc}
                  </code>
                </Field>
              </CCol>
            )}
          </CRow>
        </CCardBody>
      </CCard>

      {/* Documento Asociado */}
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Documento Asociado</strong>
        </CCardHeader>
        <CCardBody>
          {loadingAssociated ? (
            <div className="text-center py-2">
              <Spinner animation="border" size="sm" />
            </div>
          ) : (
            <CRow>
              <CCol md="6">
                <Field label="Tipo">
                  {creditNote.associatedDocumentType
                    ? ASSOCIATED_DOCUMENT_TYPE_CHOICES.get(
                        creditNote.associatedDocumentType,
                      ) ?? creditNote.associatedDocumentType
                    : "-"}
                </Field>
              </CCol>
              {isElectronicInvoice && associatedInvoice ? (
                <>
                  <CCol md="6">
                    <Field label="Número">
                      {associatedInvoice.invoiceNumber
                        ? formatInvoiceNumber(associatedInvoice)
                        : "-"}
                    </Field>
                  </CCol>
                  <CCol md="6">
                    <Field label="Timbrado">
                      {associatedInvoice.stampIdentifier}
                    </Field>
                  </CCol>
                  <CCol md="6">
                    <Field label="Fecha">
                      {associatedInvoice.invoiceDate
                        ? formatUTCDateTime(
                            new Date(associatedInvoice.invoiceDate),
                          )
                        : "-"}
                    </Field>
                  </CCol>
                  {associatedInvoice.cdc && (
                    <CCol md="12">
                      <Field label="CDC">
                        <code style={{ wordBreak: "break-all" }}>
                          {associatedInvoice.cdc}
                        </code>
                      </Field>
                    </CCol>
                  )}
                </>
              ) : (
                <>
                  <CCol md="6">
                    <Field label="Número">
                      {[
                        creditNote.associatedDocumentEstablishmentCode,
                        creditNote.associatedDocumentDispatchPointCode,
                        creditNote.associatedDocumentNumber,
                      ]
                        .filter(Boolean)
                        .join("-") || "-"}
                    </Field>
                  </CCol>
                  <CCol md="6">
                    <Field label="Timbrado">
                      {creditNote.associatedDocumentStampIdentifier}
                    </Field>
                  </CCol>
                  <CCol md="6">
                    <Field label="Fecha">
                      {creditNote.associatedDocumentDate}
                    </Field>
                  </CCol>
                  {creditNote.associatedDocumentCdc && (
                    <CCol md="12">
                      <Field label="CDC">
                        <code style={{ wordBreak: "break-all" }}>
                          {creditNote.associatedDocumentCdc}
                        </code>
                      </Field>
                    </CCol>
                  )}
                </>
              )}
            </CRow>
          )}
        </CCardBody>
      </CCard>

      {/* Contribuyente Emisor */}
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Contribuyente</strong>
        </CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="6">
              <Field label="RUC">{creditNote.taxPayerRuc}</Field>
            </CCol>
            <CCol md="6">
              <Field label="Razón Social">
                {creditNote.taxPayerSocialName}
              </Field>
            </CCol>
            <CCol md="6">
              <Field label="Nombre de Fantasía">
                {creditNote.taxPayerFantasyName}
              </Field>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      {/* Cliente */}
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Cliente</strong>
        </CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="6">
              <Field label="Razón Social">{creditNote.clientSocialName}</Field>
            </CCol>
            <CCol md="6">
              <Field label="RUC">{creditNote.clientRuc}</Field>
            </CCol>
            <CCol md="6">
              <Field label="Tipo">
                {creditNote.clientType
                  ? CLIENT_TYPE_CHOICES.get(creditNote.clientType)
                  : "-"}
              </Field>
            </CCol>
            <CCol md="6">
              <Field label="Email">{creditNote.clientEmail}</Field>
            </CCol>
            <CCol md="6">
              <Field label="Teléfono">{creditNote.clientPhoneNumber}</Field>
            </CCol>
            {clientAddress && (
              <CCol md="6">
                <Field label="Dirección">{clientAddress}</Field>
              </CCol>
            )}
          </CRow>
        </CCardBody>
      </CCard>

      {/* Condición */}
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Condición</strong>
        </CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="6">
              <Field label="Moneda">
                {creditNote.currency
                  ? CURRENCY_CHOICES.get(creditNote.currency)
                  : "-"}
              </Field>
            </CCol>
            {creditNote.currency !== PYG &&
              creditNote.pygExchangeRate != null && (
                <CCol md="6">
                  <Field label="Tipo de Cambio">
                    {formatToPyg(creditNote.pygExchangeRate)}
                  </Field>
                </CCol>
              )}
            {creditNote.obs && (
              <CCol md="12">
                <Field label="Observaciones">{creditNote.obs}</Field>
              </CCol>
            )}
          </CRow>
        </CCardBody>
      </CCard>

      {/* DNCP */}
      {creditNote.dncpContractCode && (
        <CCard className="mb-3">
          <CCardHeader>
            <strong>DNCP</strong>
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol md="6">
                <Field label="Código de Contrato">
                  {creditNote.dncpContractCode}
                </Field>
              </CCol>
              <CCol md="6">
                <Field label="Modalidad">
                  {creditNote.dncpContractModality}
                </Field>
              </CCol>
              <CCol md="6">
                <Field label="Entidad">{creditNote.dncpContractEntity}</Field>
              </CCol>
              <CCol md="6">
                <Field label="Año">{creditNote.dncpContractYear}</Field>
              </CCol>
              <CCol md="6">
                <Field label="Secuencia">
                  {creditNote.dncpContractSequence}
                </Field>
              </CCol>
              <CCol md="6">
                <Field label="Fecha">{creditNote.dncpContractDate}</Field>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      )}

      {/* Ítems */}
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Ítems</strong>
        </CCardHeader>
        <CCardBody>
          <div className="table-responsive">
            <table className="table table-striped table-bordered table-sm">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Descripción</th>
                  <th>Código</th>
                  <th>Cantidad</th>
                  <th>Precio Unit.</th>
                  <th>IVA</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {(creditNote.items ?? []).map((item, index) => (
                  <tr key={item.id ?? index}>
                    <td>{index + 1}</td>
                    <td>{item.description || "-"}</td>
                    <td>{item.code || "-"}</td>
                    <td>{item.quantity ?? "-"}</td>
                    <td>
                      {item.unitAmount != null
                        ? formatToCurrency(item.unitAmount, currency)
                        : "-"}
                    </td>
                    <td>
                      {item.ivaType
                        ? (IVA_TYPE_CHOICES.get(item.ivaType) ?? item.ivaType)
                        : "-"}
                    </td>
                    <td>
                      {item.totalAmount != null
                        ? formatToCurrency(item.totalAmount, currency)
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CCardBody>
      </CCard>

      {/* Totales */}
      <CCard className="mb-3">
        <CCardHeader>
          <strong>Totales</strong>
        </CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="8" />
            <CCol md="4">
              <Field label="Subtotal (10% IVA)">
                {creditNote.amount10Percent != null
                  ? formatToCurrency(creditNote.amount10Percent, currency)
                  : "-"}
              </Field>
              <Field label="Subtotal (5% IVA)">
                {creditNote.amount5Percent != null
                  ? formatToCurrency(creditNote.amount5Percent, currency)
                  : "-"}
              </Field>
              <Field label="Subtotal (Exento)">
                {creditNote.amountExempt != null
                  ? formatToCurrency(creditNote.amountExempt, currency)
                  : "-"}
              </Field>
              <Field label="IVA 10%">
                {creditNote.iva10Percent != null
                  ? formatToCurrency(creditNote.iva10Percent, currency)
                  : "-"}
              </Field>
              <Field label="IVA 5%">
                {creditNote.iva5Percent != null
                  ? formatToCurrency(creditNote.iva5Percent, currency)
                  : "-"}
              </Field>
              {creditNote.totalDiscount != null &&
                creditNote.totalDiscount > 0 && (
                  <Field label="Descuento">
                    {formatToCurrency(creditNote.totalDiscount, currency)}
                  </Field>
                )}
              <p className="mb-1 mt-2">
                <strong>Total:</strong>{" "}
                <h5 className="d-inline">
                  {creditNote.amountAfterDiscount != null ||
                  creditNote.amount != null
                    ? formatToCurrency(
                        creditNote.amountAfterDiscount ?? creditNote.amount!,
                        currency,
                      )
                    : "-"}
                </h5>
              </p>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      {/* Información SET */}
      {hasSetResponse && (
        <CCard className="mb-3">
          <CCardHeader>
            <strong>Respuesta SET</strong>
          </CCardHeader>
          <CCardBody>
            <CRow>
              {creditNote.setResponseCode && (
                <CCol md="6">
                  <Field label="Código de Respuesta">
                    {creditNote.setResponseCode}
                  </Field>
                </CCol>
              )}
              {creditNote.setResponseDescription && (
                <CCol md="6">
                  <Field label="Descripción">
                    {creditNote.setResponseDescription}
                  </Field>
                </CCol>
              )}
              {creditNote.setError && (
                <CCol md="12">
                  <p className="mb-1">
                    <strong>Error:</strong>
                  </p>
                  <pre
                    style={{
                      background: "var(--theme-surface-raised)",
                      padding: "8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                    }}
                  >
                    {creditNote.setError}
                  </pre>
                </CCol>
              )}
            </CRow>
          </CCardBody>
        </CCard>
      )}
    </div>
  );
};

export default CreditNoteDetailView;
